import { moneyToNumber, roundTo2, toMoney } from './decimal.js';
import { periodRate } from './day-count.js';
import type { AmortizationResult, AmortizationRow } from './types.js';
import type { DayCount, RoundingMode } from '../schemas/project.js';

export interface RemainingInput {
  /** Outstanding principal to amortize forward, Money string. */
  balance: string;
  annualRate: string;
  /** The real remaining due dates (from the existing schedule), ascending. */
  dueDates: readonly string[];
  /** Accrual base for period 1: the last settled due date, or the contract start. */
  prevDate: string;
  dayCount: DayCount;
  /**
   * Number of leading remaining periods that accrue no interest (contract still
   * inside its interest grace window). Defaults to 0 = every remaining period
   * bears interest, preserving legacy output.
   */
  graceRemaining?: number;
  roundingMode: RoundingMode;
}

function finalize(
  rows: AmortizationRow[],
  dayCount: DayCount,
  roundingMode: RoundingMode,
): AmortizationResult {
  let totalPrincipal = 0;
  let totalInterest = 0;
  let totalPaid = 0;
  for (const row of rows) {
    totalPrincipal += moneyToNumber(row.principalAmount);
    totalInterest += moneyToNumber(row.interestAmount);
    totalPaid += moneyToNumber(row.totalAmount);
  }
  return {
    method: 'french',
    dayCount,
    totalPrincipal: toMoney(totalPrincipal, roundingMode),
    totalInterest: toMoney(totalInterest, roundingMode),
    totalPaid: toMoney(totalPaid, roundingMode),
    rows,
  };
}

/**
 * Reduce-cuota: re-amortize `balance` over the same remaining due dates,
 * solving for a new (lower) level payment. Mirrors `amortizeFrench` exactly
 * (first-period rate sizes PMT; last row absorbs the rounding remainder), so a
 * full-balance call reproduces the frozen core row-for-row.
 */
export function amortizeRemainingReduceCuota(input: RemainingInput): AmortizationResult {
  const { balance, annualRate, dueDates, prevDate, dayCount, roundingMode } = input;
  const P = moneyToNumber(balance);
  const r = Number(annualRate);
  if (!Number.isFinite(r) || r < 0) throw new Error(`invalid annual rate: ${annualRate}`);
  const n = dueDates.length;
  if (n <= 0) throw new Error('reduce-cuota requires at least one remaining due date');

  // Interest grace: the first `gr` remaining periods accrue no interest. Model A:
  // C = balance / (gr + a'), a' = annuity factor over the interest-bearing tail,
  // sized from the first interest-bearing period's rate. gr=0 reduces to today.
  const gr = Math.min(Math.max(0, Math.trunc(input.graceRemaining ?? 0)), n - 1);
  const sizingPrev = gr === 0 ? prevDate : dueDates[gr - 1]!;
  const sr = periodRate(dayCount, r, sizingPrev, dueDates[gr]!);
  const m = n - gr;
  const annuity = sr === 0 ? m : (1 - Math.pow(1 + sr, -m)) / sr;
  const pmtRaw = P / (gr + annuity);

  const rows: AmortizationRow[] = [];
  let balanceNum = P;
  let prev = prevDate;
  for (let i = 0; i < n; i++) {
    const dueDate = dueDates[i]!;
    const pr = i < gr ? 0 : periodRate(dayCount, r, prev, dueDate);
    const interest = roundTo2(balanceNum * pr, roundingMode);
    const isLast = i === n - 1;
    const principalAmt = isLast
      ? roundTo2(balanceNum, roundingMode)
      : roundTo2(roundTo2(pmtRaw, roundingMode) - interest, roundingMode);
    const totalAmt = roundTo2(principalAmt + interest, roundingMode);
    const opening = roundTo2(balanceNum, roundingMode);
    const closing = roundTo2(balanceNum - principalAmt, roundingMode);
    rows.push({
      installmentNumber: i + 1,
      dueDate,
      openingBalance: opening.toFixed(2),
      principalAmount: principalAmt.toFixed(2),
      interestAmount: interest.toFixed(2),
      totalAmount: totalAmt.toFixed(2),
      closingBalance: closing.toFixed(2),
    });
    balanceNum = balanceNum - principalAmt;
    prev = dueDate;
  }
  return finalize(rows, dayCount, roundingMode);
}

/**
 * Reduce-plazo: keep paying `fixedPayment` until `balance` is exhausted,
 * consuming a prefix of `dueDates`. The final installment is a stub
 * (principal = remaining balance, total < fixedPayment). Since a smaller
 * balance at the same payment always amortizes in <= the original count, the
 * prefix never overruns the supplied due dates.
 */
export function amortizeRemainingFixedPayment(
  input: RemainingInput & { fixedPayment: string },
): AmortizationResult {
  const { balance, annualRate, dueDates, prevDate, dayCount, roundingMode, fixedPayment } = input;
  const r = Number(annualRate);
  if (!Number.isFinite(r) || r < 0) throw new Error(`invalid annual rate: ${annualRate}`);
  if (dueDates.length === 0) throw new Error('reduce-plazo requires remaining due dates');
  const pmt = moneyToNumber(fixedPayment);
  let balanceNum = moneyToNumber(balance);
  if (balanceNum <= 0) throw new Error('reduce-plazo requires a positive balance');

  const firstRate = periodRate(dayCount, r, prevDate, dueDates[0]!);
  const firstInterest = roundTo2(balanceNum * firstRate, roundingMode);
  if (pmt <= firstInterest) {
    throw new Error('fixed payment does not cover interest; loan would not amortize');
  }

  // The first `gr` remaining periods are inside the interest grace window. The
  // guard above intentionally checks period 0's full-balance interest, which is
  // conservative here (grace pays the balance down first), so a payment that
  // originally covered interest still passes.
  const gr = Math.min(Math.max(0, Math.trunc(input.graceRemaining ?? 0)), dueDates.length - 1);

  const rows: AmortizationRow[] = [];
  let prev = prevDate;
  for (let i = 0; i < dueDates.length && balanceNum > 0.005; i++) {
    const dueDate = dueDates[i]!;
    const pr = i < gr ? 0 : periodRate(dayCount, r, prev, dueDate);
    const interest = roundTo2(balanceNum * pr, roundingMode);
    let principalAmt = roundTo2(pmt - interest, roundingMode);
    if (principalAmt >= balanceNum - 0.005) {
      principalAmt = roundTo2(balanceNum, roundingMode); // final stub
    }
    const totalAmt = roundTo2(principalAmt + interest, roundingMode);
    const opening = roundTo2(balanceNum, roundingMode);
    const closing = roundTo2(balanceNum - principalAmt, roundingMode);
    rows.push({
      installmentNumber: i + 1,
      dueDate,
      openingBalance: opening.toFixed(2),
      principalAmount: principalAmt.toFixed(2),
      interestAmount: interest.toFixed(2),
      totalAmount: totalAmt.toFixed(2),
      closingBalance: closing.toFixed(2),
    });
    balanceNum = roundTo2(balanceNum - principalAmt, roundingMode);
    prev = dueDate;
  }
  if (balanceNum > 0.005) {
    throw new Error('remaining due dates insufficient to amortize at the fixed payment');
  }
  return finalize(rows, dayCount, roundingMode);
}
